import {apiClient} from "../../shared/api/client";
import {downloadAuthenticatedFile} from "../../shared/finance/downloads";
import {
    SettlementContractError,
    normalizeSettlementDetail,
    normalizeSettlementDraft,
    normalizeSettlementList,
    normalizeSettlementMutation,
    normalizeSettlementPayouts,
    normalizeSettlementReport,
} from "./contracts";

const SETTLEMENTS_PATH = "/api/v2/admin/settlements";
const SETTLEMENT_EXPORTS_PATH = "/api/v2/admin/settlement-exports";

// Серверный дефолт списков (sorpa-backend#19, clamp_admin_page_size): при таком
// значении page_size не отправляется, а ключ кэша совпадает с историческим.
export const DEFAULT_SETTLEMENT_PAGE_SIZE = 20;

export const settlementKeys = {
    all: (scope) => ["settlements", String(scope)],
    list: (scope, filters, page, pageSize = DEFAULT_SETTLEMENT_PAGE_SIZE) =>
        pageSize === DEFAULT_SETTLEMENT_PAGE_SIZE
            ? ["settlements", String(scope), "list", filters, page]
            : ["settlements", String(scope), "list", filters, page, pageSize],
    detail: (scope, id) => ["settlements", String(scope), "detail", Number(id)],
    payouts: (scope, id) => ["settlements", String(scope), "payouts", Number(id)],
    exports: (scope, page) => ["settlements", String(scope), "exports", page],
    exportDetail: (scope, id) => ["settlements", String(scope), "export", Number(id)],
    companies: (scope, q = "") => ["settlements", String(scope), "companies", String(q || "")],
    companyBranches: (scope, companyId) => [
        "settlements",
        String(scope),
        "company-branches",
        Number(companyId),
    ],
};

const cleanParams = (values) =>
    Object.fromEntries(
        Object.entries(values || {}).filter(
            ([, value]) => value !== "" && value !== null && value !== undefined,
        ),
    );

const numericId = (value) => Number(value);

const positiveInteger = (value, label) => {
    const text = String(value ?? "").trim();
    if (!/^[1-9]\d*$/.test(text)) {
        throw new SettlementContractError(`${label} must be a positive integer`);
    }
    return Number(text);
};

const optionalPositiveInteger = (value, label) =>
    value === "" || value == null ? undefined : positiveInteger(value, label);

const calendarDate = (value, label) => {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        throw new SettlementContractError(`${label} must be a calendar date`);
    }
    const parsed = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
        throw new SettlementContractError(`${label} must be a calendar date`);
    }
    return value;
};

const period = (periodStart, periodEnd) => {
    const start = calendarDate(periodStart, "period_start");
    const end = calendarDate(periodEnd, "period_end");
    if (start >= end) {
        throw new SettlementContractError("period_start must be before period_end");
    }
    return {start, end};
};

const isoPeriod = (periodStart, periodEnd) => {
    const dates = period(periodStart, periodEnd);
    return {
        start: `${dates.start}T00:00:00Z`,
        end: `${dates.end}T00:00:00Z`,
    };
};

const isoTimestamp = (value, label, {nullable = false} = {}) => {
    if (nullable && value === null) return null;
    if (
        typeof value !== "string" ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) ||
        Number.isNaN(Date.parse(value))
    ) {
        throw new SettlementContractError(`${label} must be an ISO timestamp${nullable ? " or null" : ""}`);
    }
    return value;
};

const nullableText = (value, label) => {
    if (value === null) return null;
    if (typeof value !== "string") throw new SettlementContractError(`${label} must be text or null`);
    return value;
};

const nonNegativeInteger = (value, label) => {
    const number = typeof value === "number" ? value : Number(value);
    if (!Number.isInteger(number) || number < 0) {
        throw new SettlementContractError(`${label} must be a non-negative integer`);
    }
    return number;
};

const nullablePositiveInteger = (value, label) =>
    value === null ? null : positiveInteger(value, label);

const positiveIntegerArray = (value, label) => {
    if (!Array.isArray(value)) throw new SettlementContractError(`${label} must be an array`);
    return value.map((item, index) => positiveInteger(item, `${label}[${index}]`));
};

const exportPageValue = (value, label) => {
    if (value === null) return null;
    if (typeof value !== "string" || !value.trim()) {
        throw new SettlementContractError(`${label} must be a page URL or null`);
    }
    let url;
    try {
        url = new URL(value, "https://settlement-exports.invalid");
    } catch (_error) {
        throw new SettlementContractError(`${label} must be a valid page URL`);
    }
    if (!url.pathname.endsWith(SETTLEMENT_EXPORTS_PATH)) {
        throw new SettlementContractError(`${label} must target the settlement export list`);
    }
    const pages = url.searchParams.getAll("page");
    if (pages.length !== 1 || !/^[1-9]\d*$/.test(pages[0])) {
        throw new SettlementContractError(`${label} must contain one positive page`);
    }
    return Number(pages[0]);
};

const reportSummary = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new SettlementContractError("summary must be an object");
    }
    const summary = {
        total_count: nonNegativeInteger(value.total_count, "summary.total_count"),
        ready_count: nonNegativeInteger(value.ready_count, "summary.ready_count"),
        not_ready_count: nonNegativeInteger(value.not_ready_count, "summary.not_ready_count"),
        total_payable_amount: value.total_payable_amount,
        ready_payable_amount: value.ready_payable_amount,
    };
    if (!/^-?(?:0|[1-9]\d*)\.\d{2}$/.test(summary.total_payable_amount)) {
        throw new SettlementContractError("summary.total_payable_amount must be canonical money");
    }
    if (!/^-?(?:0|[1-9]\d*)\.\d{2}$/.test(summary.ready_payable_amount)) {
        throw new SettlementContractError("summary.ready_payable_amount must be canonical money");
    }
    return summary;
};

const exportRecord = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new SettlementContractError("settlement export must be an object");
    }
    const record = {
        id: positiveInteger(value.id, "id"),
        country_id: nullablePositiveInteger(value.country_id, "country_id"),
        period_start: isoTimestamp(value.period_start, "period_start"),
        period_end: isoTimestamp(value.period_end, "period_end"),
        status: typeof value.status === "string" && value.status.trim()
            ? value.status
            : (() => { throw new SettlementContractError("status must be text"); })(),
        render_status: typeof value.render_status === "string" && ["pending", "rendering", "finalized", "failed"].includes(value.render_status)
            ? value.render_status
            : (() => { throw new SettlementContractError("render_status has an unknown value"); })(),
        format: typeof value.format === "string" && value.format.trim()
            ? value.format
            : (() => { throw new SettlementContractError("format must be text"); })(),
        downloadable: value.downloadable === true || value.downloadable === false
            ? value.downloadable
            : (() => { throw new SettlementContractError("downloadable must be boolean"); })(),
        filename: nullableText(value.filename, "filename"),
        included_statement_ids: positiveIntegerArray(value.included_statement_ids, "included_statement_ids"),
        excluded_statement_ids: positiveIntegerArray(value.excluded_statement_ids, "excluded_statement_ids"),
        created_by_id: nullablePositiveInteger(value.created_by_id, "created_by_id"),
        sha256: nullableText(value.sha256, "sha256"),
        size_bytes: value.size_bytes === null ? null : nonNegativeInteger(value.size_bytes, "size_bytes"),
        finalized_at: isoTimestamp(value.finalized_at, "finalized_at", {nullable: true}),
        created_at: isoTimestamp(value.created_at, "created_at"),
    };
    if (record.period_start >= record.period_end) {
        throw new SettlementContractError("period_start must be before period_end");
    }
    return record;
};

const exportResult = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value) || value.success !== true) {
        throw new SettlementContractError("settlement export.success must be true");
    }
    if (typeof value.format !== "string" || !value.format.trim()) {
        throw new SettlementContractError("format must be text");
    }
    return {
        success: true,
        export_id: positiveInteger(value.export_id, "export_id"),
        format: value.format,
        included_statement_ids: positiveIntegerArray(value.included_statement_ids, "included_statement_ids"),
        excluded_statement_ids: positiveIntegerArray(value.excluded_statement_ids, "excluded_statement_ids"),
        summary: reportSummary(value.summary),
    };
};

export const listSettlements = async ({
    countryScope,
    filters = {},
    page = 1,
    pageSize = DEFAULT_SETTLEMENT_PAGE_SIZE,
}) => {
    const response = await apiClient.get(SETTLEMENTS_PATH, {
        params: cleanParams({
            country_id: String(countryScope),
            page,
            page_size:
                pageSize === DEFAULT_SETTLEMENT_PAGE_SIZE ? undefined : pageSize,
            ...filters,
        }),
    });
    return normalizeSettlementList(response.data);
};

export const listSettlementCompanies = async ({countryScope, q = ""}) => {
    const response = await apiClient.get(`${SETTLEMENTS_PATH}/companies`, {
        params: cleanParams({
            country_id: String(countryScope),
            q: q || undefined,
        }),
    });
    const results = Array.isArray(response.data?.results) ? response.data.results : [];
    return results.map((item) => ({
        id: positiveInteger(item.id, "company.id"),
        name: typeof item.name === "string" ? item.name : `#${item.id}`,
        bin_number: typeof item.bin_number === "string" ? item.bin_number : "",
    }));
};

export const listSettlementCompanyBranches = async ({countryScope, companyId}) => {
    const id = positiveInteger(companyId, "company_id");
    const response = await apiClient.get(`${SETTLEMENTS_PATH}/companies/${id}/branches`, {
        params: cleanParams({
            country_id: String(countryScope),
        }),
    });
    const results = Array.isArray(response.data?.results) ? response.data.results : [];
    return results.map((item) => ({
        id: positiveInteger(item.id, "branch.id"),
        name: typeof item.name === "string" ? item.name : `#${item.id}`,
    }));
};

export const getSettlement = async ({settlementId, countryScope}) => {
    const response = await apiClient.get(`${SETTLEMENTS_PATH}/${numericId(settlementId)}`, {
        params: {country_id: String(countryScope)},
    });
    return normalizeSettlementDetail(response.data);
};

export const buildSettlementReport = async ({
    countryScope,
    periodStart,
    periodEnd,
    companyId,
    branchId,
}) => {
    const dates = isoPeriod(periodStart, periodEnd);
    const response = await apiClient.post(
        `${SETTLEMENTS_PATH}/report`,
        cleanParams({
            period_start: dates.start,
            period_end: dates.end,
            country_id: positiveInteger(countryScope, "country_id"),
            company_id: optionalPositiveInteger(companyId, "company_id"),
            branch_id: optionalPositiveInteger(branchId, "branch_id"),
        }),
    );
    return normalizeSettlementReport(response.data);
};

export const createSettlementDraft = async ({companyId, branchId, periodStart, periodEnd}) => {
    const dates = isoPeriod(periodStart, periodEnd);
    const response = await apiClient.post(
        `${SETTLEMENTS_PATH}/draft`,
        cleanParams({
            company_id: positiveInteger(companyId, "company_id"),
            period_start: dates.start,
            period_end: dates.end,
            branch_id: optionalPositiveInteger(branchId, "branch_id"),
        }),
    );
    return normalizeSettlementDraft(response.data);
};

export const exportSettlements = async ({
    countryScope,
    periodStart,
    periodEnd,
    companyId,
    branchId,
}) => {
    const dates = isoPeriod(periodStart, periodEnd);
    const response = await apiClient.post(
        `${SETTLEMENTS_PATH}/export`,
        cleanParams({
            period_start: dates.start,
            period_end: dates.end,
            country_id: positiveInteger(countryScope, "country_id"),
            company_id: optionalPositiveInteger(companyId, "company_id"),
            branch_id: optionalPositiveInteger(branchId, "branch_id"),
        }),
    );
    return exportResult(response.data);
};

export const listSettlementExports = async ({page = 1} = {}) => {
    const response = await apiClient.get(SETTLEMENT_EXPORTS_PATH, {
        params: {page: positiveInteger(page, "page")},
    });
    const payload = response.data;
    if (!payload || typeof payload !== "object" || Array.isArray(payload) || !Array.isArray(payload.results)) {
        throw new SettlementContractError("settlement export page must be an object with results");
    }
    return {
        count: nonNegativeInteger(payload.count, "count"),
        next: exportPageValue(payload.next, "next"),
        previous: exportPageValue(payload.previous, "previous"),
        results: payload.results.map(exportRecord),
    };
};

export const getSettlementExport = async ({exportId}) => {
    const response = await apiClient.get(`${SETTLEMENT_EXPORTS_PATH}/${positiveInteger(exportId, "export_id")}`);
    return exportRecord(response.data);
};

export const downloadSettlementExport = ({exportId}) => {
    const id = positiveInteger(exportId, "export_id");
    return downloadAuthenticatedFile({
        url: `${SETTLEMENT_EXPORTS_PATH}/${id}/download`,
        fallbackName: `settlement-export-${id}.xlsx`,
    });
};

const mutation = async (settlementId, suffix, payload, {withoutBody = false} = {}) => {
    const url = `${SETTLEMENTS_PATH}/${numericId(settlementId)}/${suffix}`;
    const response = withoutBody
        ? await apiClient.post(url)
        : await apiClient.post(url, payload);
    return normalizeSettlementMutation(response.data);
};

export const approveSettlement = ({settlementId}) =>
    mutation(settlementId, "approve", undefined, {withoutBody: true});

export const reopenSettlement = ({settlementId, payload}) =>
    mutation(settlementId, "reopen", payload);

export const cancelSettlement = ({settlementId, payload}) =>
    mutation(settlementId, "cancel", payload);

export const holdSettlement = ({settlementId, payload}) =>
    mutation(settlementId, "hold", payload);

export const releaseSettlementHold = ({settlementId, payload}) =>
    mutation(settlementId, "release-hold", payload);

export const createSettlementAdjustment = ({settlementId, payload}) =>
    mutation(settlementId, "adjustments", payload);

export const sendSettlementToBank = ({settlementId, payload}) =>
    mutation(settlementId, "send-to-bank", payload);

export const markSettlementPartiallyPaid = ({settlementId, payload}) =>
    mutation(settlementId, "partial-paid", payload);

export const markSettlementPaid = ({settlementId, payload}) =>
    mutation(settlementId, "mark-paid", payload);

export const listSettlementPayouts = async ({settlementId}) => {
    const response = await apiClient.get(
        `${SETTLEMENTS_PATH}/${numericId(settlementId)}/payouts`,
    );
    return normalizeSettlementPayouts(response.data);
};

export const loadSettlements = (filters = {}) =>
    listSettlements({
        countryScope: filters.country_id ?? "0",
        page: filters.page ?? 1,
        filters,
    });

export const loadSettlementDetail = (settlementId, countryScope = "0") =>
    getSettlement({settlementId, countryScope});
